import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { brandingAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Building2, Mail, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';

// Helper to get full URL for uploaded assets
const getAssetUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  // Convert /uploads/ to /api/uploads/ for proper routing through ingress
  const apiPath = path.startsWith('/uploads/') ? `/api${path}` : path;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  return `${backendUrl}${apiPath}`;
};

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [branding, setBranding] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get redirect path from location state or sessionStorage
  const from = location.state?.from?.pathname || sessionStorage.getItem('lastVisitedPath') || '/dashboard';

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      const res = await brandingAPI.get();
      setBranding(res.data);
    } catch (error) {
      // Use defaults if branding fetch fails
      console.log('Using default branding');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await login({ email, password, remember_me: rememberMe });
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  // Default values
  const companyName = branding?.company_name || 'UnifiCRM';
  const tagline = branding?.company_tagline || 'Unified platform for customer relationships, sales, and support.';
  const loginTitle = branding?.login_title || 'Welcome back';
  const loginSubtitle = branding?.login_subtitle || 'Enter your credentials to access your account';
  const bgColor = branding?.login_background_color || '#f8fafc';
  const cardStyle = branding?.login_card_style || 'default';
  const primaryColor = branding?.primary_color || '#3b82f6';
  const showPoweredBy = branding?.show_powered_by !== false;
  const logoUrl = getAssetUrl(branding?.logo_url);

  const getCardClasses = () => {
    switch (cardStyle) {
      case 'glass':
        return 'backdrop-blur-lg bg-white/90 shadow-xl border-white/20';
      case 'minimal':
        return 'border-0 shadow-none bg-white';
      case 'gradient':
        return 'border-2 border-transparent bg-clip-padding shadow-lg';
      default:
        return 'shadow-lg';
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Brand Side */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 text-white p-12 flex-col justify-between">
        <div>
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img src={logoUrl} alt={companyName} className="h-10 object-contain" />
            ) : (
              <>
                <Building2 className="h-8 w-8" strokeWidth={1.5} />
                <span className="text-2xl font-bold font-['Manrope']">{companyName}</span>
              </>
            )}
          </div>
        </div>
        <div className="space-y-6">
          <blockquote className="text-3xl font-semibold font-['Manrope'] heading-tight">
            "{tagline}"
          </blockquote>
          <p className="text-slate-400">
            Streamline your business operations with our all-in-one CRM solution.
          </p>
        </div>
        <div className="text-sm text-slate-500">
          © {new Date().getFullYear()} {companyName}. All rights reserved.
        </div>
      </div>

      {/* Form Side */}
      <div 
        className="flex-1 flex items-center justify-center p-8 transition-colors"
        style={{ backgroundColor: bgColor }}
      >
        <Card className={`w-full max-w-md ${getCardClasses()}`}>
          <CardHeader className="space-y-1">
            <div className="lg:hidden flex items-center gap-2 mb-4 justify-center">
              {logoUrl ? (
                <img src={logoUrl} alt={companyName} className="h-8 object-contain" />
              ) : (
                <>
                  <Building2 className="h-6 w-6" strokeWidth={1.5} />
                  <span className="text-xl font-bold font-['Manrope']">{companyName}</span>
                </>
              )}
            </div>
            <CardTitle className="text-2xl font-['Manrope']">{loginTitle}</CardTitle>
            <CardDescription>{loginSubtitle}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@company.com"
                    className="pl-10"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" strokeWidth={1.5} />
                    ) : (
                      <Eye className="h-4 w-4" strokeWidth={1.5} />
                    )}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="remember-me" 
                    checked={rememberMe}
                    onCheckedChange={setRememberMe}
                    data-testid="remember-me-checkbox"
                  />
                  <label 
                    htmlFor="remember-me" 
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    Remember me for 30 days
                  </label>
                </div>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-blue-600 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
                data-testid="login-submit-btn"
                style={{ backgroundColor: primaryColor }}
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">Don't have an account? </span>
              <Link 
                to="/register" 
                className="hover:underline font-medium" 
                style={{ color: primaryColor }}
                data-testid="register-link"
              >
                Sign up
              </Link>
            </div>
            {/* Customer Portal Link */}
            <div className="mt-4 pt-4 border-t text-center">
              <p className="text-sm text-muted-foreground">
                Are you a customer?{' '}
                <Link 
                  to="/customer-login" 
                  className="text-blue-600 hover:underline font-medium"
                  data-testid="customer-portal-link"
                >
                  Go to Customer Portal
                </Link>
              </p>
            </div>
            {showPoweredBy && (
              <div className="mt-4 pt-4 border-t text-center">
                <p className="text-xs text-muted-foreground">
                  Powered by {companyName}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
